import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import { useLang } from '../context/LanguageContext';

const ageOptions = [
  { id: '18-29', label: '18-29' },
  { id: '30-39', label: '30-39' },
  { id: '40-49', label: '40-49' },
  { id: '50+', label: '50+' }
];

const LandingPage = ({ updateAnswer }) => {
  const navigate = useNavigate();
  const { t } = useLang();

  const handleSelect = (age) => {
    updateAnswer('ageGroup', age);
    navigate('/quiz');
  };

  const stats = [
    { num: '10M+', label: t('downloads') },
    { num: '4.7', label: t('appRating') },
    { num: '95%', label: t('successRate') },
    { num: '250K+', label: t('reviews') },
  ];

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />

      <main className="px-4 md:px-8 pb-20" data-testid="landing-main">
        <div className="max-w-6xl mx-auto">
          <div className="text-center pt-6 md:pt-10 fade-in-up">
            <h1 className="heading-font text-5xl md:text-7xl font-black tracking-wide leading-tight">
              {t('landingHero1')}<br />{t('landingHero2')}
            </h1>
            <p className="mt-4 text-base md:text-lg text-white/90 max-w-xl mx-auto">
              {t('landingSub')}
            </p>
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="flex justify-center md:justify-end order-2 md:order-1">
              <div className="relative w-full max-w-md">
                <img
                  src="https://images.unsplash.com/photo-1683509602596-f04aca698097"
                  alt="Fit couple"
                  className="w-full h-auto object-cover rounded-md"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0e10] via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            <div className="order-1 md:order-2 w-full max-w-md mx-auto md:mx-0">
              <h2 className="text-center text-[#ff4e15] text-lg font-bold mb-5 tracking-wide">
                {t('selectYourAge')}
              </h2>
              <div className="space-y-3">
                {ageOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => handleSelect(opt.id)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-[#1a1b1e] hover:bg-[#242528] border border-transparent hover:border-[#ff4e15] rounded-sm text-white text-lg font-medium transition-all group"
                    data-testid={`age-${opt.id}-btn`}
                  >
                    <span>{opt.label}</span>
                    <span className="w-9 h-9 flex items-center justify-center rounded-sm bg-[#0d0e10] border border-[#333] group-hover:border-[#ff4e15] group-hover:bg-[#ff4e15] transition-all">
                      <ArrowRight size={18} className="text-white" />
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-6 text-xs text-white/60 leading-relaxed">
                <p>
                  {t('landingTermsPre')}{' '}
                  <a href="/terms" className="text-[#ff4e15] underline">{t('termsConditions')}</a>,{' '}
                  <a href="/privacy-policy" className="text-[#ff4e15] underline">{t('privacyPolicy')}</a>,{' '}
                  <a href="/refund-policy" className="text-[#ff4e15] underline">{t('refundPolicy')}</a>{' '}
                  {t('landingTermsAnd')}{' '}
                  <a href="/shipping-return" className="text-[#ff4e15] underline">{t('shippingReturn')}</a>{' '}
                  {t('landingTermsMeta')}
                </p>
                <p className="mt-3 flex items-start gap-2">
                  <ShieldCheck size={14} className="text-white/60 mt-0.5 shrink-0" />
                  {t('landingMedical')}
                </p>
              </div>
            </div>
          </div>

          {/* Trust / social proof strip */}
          <div className="mt-16 border-t border-[#1f2024] pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-[#ff4e15] text-2xl md:text-3xl font-black heading-font">{s.num}</div>
                <div className="text-white/70 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
